import type { ModuleUpgradeChangeSummary } from '@/common/api/models/moduleUpgradeChangeSummary';
import type { ModuleUpgradeOffer } from '@/common/api/models/moduleUpgradeOffer';

export const formatModuleVersionLabel = (version: string): string => {
  const trimmed = version.trim();
  return trimmed.startsWith('v') ? trimmed : `v${trimmed}`;
};

type TaskTypeKey = 'essay' | 'oral_exam' | 'activity' | 'multiple_choice' | 'event';

const TASK_TYPE_KEYS: TaskTypeKey[] = [
  'essay',
  'oral_exam',
  'activity',
  'multiple_choice',
  'event',
];

export const sumTypeCounts = (counts: Record<string, number> | undefined): number => (
  Object.values(counts ?? {}).reduce((total, count) => total + count, 0)
);

export const hasAnyTypeCount = (counts: Record<string, number> | undefined): boolean => (
  sumTypeCounts(counts) > 0
);

export const getUpgradeOfferTitleKey = (
  changeSummary: ModuleUpgradeChangeSummary | null | undefined,
): string => {
  if ((changeSummary?.aiTutorGainedByType?.essay ?? 0) > 0) {
    return 'student.module.upgrade-offer.title-ai-tutor-essays';
  }
  if (hasAnyTypeCount(changeSummary?.addedByType)) {
    return 'student.module.upgrade-offer.title-new-version';
  }
  return 'student.module.upgrade-offer.modal-title';
};

export const gradeBasisEqual = (
  before: Record<string, number> | undefined,
  after: Record<string, number> | undefined,
): boolean => {
  const keys = new Set([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);
  return [...keys].every((key) => (before?.[key] ?? 0) === (after?.[key] ?? 0));
};

export const formatGradeBasisLine = (
  t: (key: string, options?: Record<string, unknown>) => string,
  basisByType: Record<string, number> | undefined,
): string => {
  const parts = TASK_TYPE_KEYS
    .map((typeKey) => {
      const count = basisByType?.[typeKey] ?? 0;
      if (count <= 0) {
        return null;
      }
      const labelKey = count === 1
        ? `student.module.upgrade-offer.type-label.${typeKey}.one`
        : `student.module.upgrade-offer.type-label.${typeKey}.other`;
      return t(labelKey, { count });
    })
    .filter(Boolean);

  return parts.join(', ');
};

export const getAddedNonGradeBenefitLines = (
  t: (key: string, options?: Record<string, unknown>) => string,
  changeSummary: ModuleUpgradeChangeSummary,
  gradeBasisAfter: Record<string, number> | undefined,
): string[] => {
  const lines: string[] = [];

  TASK_TYPE_KEYS.forEach((typeKey) => {
    const addedCount = changeSummary.addedByType?.[typeKey] ?? 0;
    if (addedCount <= 0) {
      return;
    }
    const gradeCount = gradeBasisAfter?.[typeKey] ?? 0;
    if (gradeCount > 0) {
      return;
    }
    const labelKey = addedCount === 1
      ? `student.module.upgrade-offer.added-type.${typeKey}.one`
      : `student.module.upgrade-offer.added-type.${typeKey}.other`;
    lines.push(t(labelKey, { count: addedCount }));
  });

  return lines;
};

export const buildUpgradeOfferDerivedCopy = (
  t: (key: string, options?: Record<string, unknown>) => string,
  upgradeOffer: ModuleUpgradeOffer,
) => {
  const summary = upgradeOffer.changeSummary;
  const whatYouGet: string[] = [];

  if (summary) {
    const aiTutorEssays = summary.aiTutorGainedByType?.essay ?? 0;
    if (aiTutorEssays > 0) {
      const key = aiTutorEssays === 1
        ? 'student.module.upgrade-offer.ai-tutor-essays.one'
        : 'student.module.upgrade-offer.ai-tutor-essays.other';
      whatYouGet.push(t(key, { count: aiTutorEssays }));
    }

    whatYouGet.push(
      ...getAddedNonGradeBenefitLines(t, summary, summary.gradeBasisAfterByType),
    );

    if (summary.hasFinishedWorkThatStopsCounting) {
      whatYouGet.push(t('student.module.upgrade-offer.finished-work-stays'));
    }
  }

  return {
    titleKey: getUpgradeOfferTitleKey(summary),
    whatYouGet,
    gradeBasisUnchanged: summary
      ? gradeBasisEqual(summary.gradeBasisBeforeByType, summary.gradeBasisAfterByType)
      : false,
    gradeBasisBeforeLine: formatGradeBasisLine(t, summary?.gradeBasisBeforeByType),
    gradeBasisAfterLine: formatGradeBasisLine(t, summary?.gradeBasisAfterByType),
    showOralOnlyWarning: summary?.gradeBecomesOralOnly ?? false,
    summaryUnavailable: !summary,
  };
};
